import { Plus, Trash2 } from 'lucide-react';

/* Editor for the accounting subscription plans persisted at `pricing/current.packages`
   — the cards on the public /accounting/pricing page and the Accounting landing.

   Presentational only: the parent owns the packages state plus loading and saving. That
   lets the same editor appear in two places without duplicating the UI —
   /admin/pricing saves it alongside fees & bands in one write, while the Package
   Pricing page saves it on its own with a merge write. Both target the same Firestore
   field, so whichever you edit, the other reflects it. */

export type Pkg = {
  name: string;
  tag: string;
  desc: string;
  inherit: string;
  features: string[];
  priceYr: number;
  priceMo: number | null;
  discount: string | null;
  trial: string | null;
  govFees: boolean;
  popular: boolean;
};

export const NEW_PKG = (): Pkg => ({
  name: 'New plan', tag: '', desc: '', inherit: 'Includes:', features: [],
  priceYr: 0, priceMo: null, discount: null, trial: null, govFees: true, popular: false,
});

export const DEFAULT_PACKAGES: Pkg[] = [
  { name: 'Pulse', tag: '(for Existing Businesses)', desc: 'For founders who already have a registered business and want to stay compliant, investor-ready, and growth-focused with the right legal and financial tools.', inherit: 'Includes:', features: ['Business Health & Investor Readiness Report', 'Legal & Compliance Check (Trade License, MOA, VAT)', 'Corporate Tax Registration & Advisory', 'Access to Business Templates (NDAs, MOA, Resolutions)'], priceYr: 1100, priceMo: null, discount: null, trial: '30-Day Free Trial', govFees: true, popular: false },
  { name: 'Total Compliance', tag: '', desc: 'For entrepreneurs who want to stay 100% compliant with UAE regulations.', inherit: 'Everything in Pulse +', features: ['VAT registration + quarterly VAT filings', 'Accounting & bookkeeping dashboard (cash flow, P&L, balance sheet)', 'UAE Corporate Tax (CT) registration & annual return filing (FTA)', 'Includes up to 100 bookkeeping transactions per month', 'Automated transaction integrations (Stripe, Shopify, Lean, etc.)', 'Annual financial statements (CFO-ready)'], priceYr: 14399, priceMo: 1499, discount: '20% OFF', trial: null, govFees: true, popular: false },
  { name: 'Business-in-a-Box™', tag: '', desc: 'For entrepreneurs who want full legal, accounting, and tax compliance, plus a dedicated accountant.', inherit: 'Everything in Total Compliance +', features: ['Corporate Tax (CT) registration & annual return filing (FTA) + quarterly tax planning & estimates', 'Includes up to 200 bookkeeping transactions per month', 'Quarterly compliance advisory session', 'Investor-ready reporting (deck financials, data room prep)', 'Priority support'], priceYr: 22999, priceMo: 2399, discount: '20% OFF', trial: null, govFees: true, popular: true },
];

const txtCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400';
const numCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-bold bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400';
const areaCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm bg-amber-50 text-gray-900 focus:outline-none focus:border-amber-400 leading-relaxed';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

export default function AccountingPackagesEditor({
  packages,
  onChange,
}: {
  packages: Pkg[];
  onChange: (next: Pkg[]) => void;
}) {
  // Clone before mutating so the caller's previous state object is never touched
  // (features is copied too — a shared array reference would leak edits).
  const patchPkg = (i: number, fn: (p: Pkg) => void) => {
    const next = packages.map(x => ({ ...x, features: [...x.features] }));
    const target = next[i];
    if (!target) return;
    fn(target);
    onChange(next);
  };
  const addPkg = () => onChange([...packages, NEW_PKG()]);
  const delPkg = (i: number) => onChange(packages.filter((_, j) => j !== i));

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-amber-400 to-amber-500" />
      <div className="p-5">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Subscription plans</div>
        <p className="text-[11px] text-gray-400 font-medium mb-4">Shown on the public <b>/accounting/pricing</b> page &amp; the Accounting landing. One feature per line.</p>
        <div className="space-y-4">
          {packages.map((p, i) => (
            <div key={i} className="border-2 border-gray-100 rounded-xl p-4 relative">
              <button onClick={() => delPkg(i)} className="absolute top-3 right-3 text-gray-300 hover:text-red-500 transition-colors p-1" title="Remove plan"><Trash2 className="w-4 h-4" /></button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className={lbl}>Name</label>
                  <input value={p.name} onChange={e => patchPkg(i, d => { d.name = e.target.value; })} className={txtCls} /></div>
                <div><label className={lbl}>Tag (optional)</label>
                  <input value={p.tag} onChange={e => patchPkg(i, d => { d.tag = e.target.value; })} placeholder="(for Existing Businesses)" className={txtCls} /></div>
              </div>
              <div className="mt-3"><label className={lbl}>Description</label>
                <input value={p.desc} onChange={e => patchPkg(i, d => { d.desc = e.target.value; })} className={txtCls} /></div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div><label className={lbl}>Price / yr (AED)</label>
                  <input type="number" min={0} value={p.priceYr} onChange={e => patchPkg(i, d => { d.priceYr = Math.max(0, +e.target.value || 0); })} className={numCls} /></div>
                <div><label className={lbl}>Price / mo (AED)</label>
                  <input type="number" min={0} value={p.priceMo ?? ''} placeholder="annual only" onChange={e => patchPkg(i, d => { d.priceMo = e.target.value === '' ? null : Math.max(0, +e.target.value || 0); })} className={numCls} /></div>
                <div><label className={lbl}>Discount label</label>
                  <input value={p.discount ?? ''} onChange={e => patchPkg(i, d => { d.discount = e.target.value || null; })} placeholder="e.g. 20% OFF" className={txtCls} /></div>
                <div><label className={lbl}>Trial label</label>
                  <input value={p.trial ?? ''} onChange={e => patchPkg(i, d => { d.trial = e.target.value || null; })} placeholder="e.g. 30-Day Free Trial" className={txtCls} /></div>
              </div>
              <div className="mt-3"><label className={lbl}>Features heading</label>
                <input value={p.inherit} onChange={e => patchPkg(i, d => { d.inherit = e.target.value; })} placeholder="e.g. Everything in Pulse +" className={txtCls} /></div>
              <div className="mt-3"><label className={lbl}>Features (one per line)</label>
                <textarea value={p.features.join('\n')} onChange={e => patchPkg(i, d => { d.features = e.target.value.split('\n').map(s => s.trimEnd()).filter(Boolean); })} rows={Math.max(3, p.features.length)} className={areaCls} /></div>
              <div className="flex gap-5 mt-3">
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><input type="checkbox" checked={p.govFees} onChange={e => patchPkg(i, d => { d.govFees = e.target.checked; })} /> Show “+ government fees”</label>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><input type="checkbox" checked={p.popular} onChange={e => patchPkg(i, d => { d.popular = e.target.checked; })} /> Most popular</label>
              </div>
            </div>
          ))}
        </div>
        <button onClick={addPkg} className="flex items-center gap-1.5 text-amber-700 hover:text-amber-800 font-bold text-xs mt-3"><Plus className="w-4 h-4" /> Add plan</button>
      </div>
    </div>
  );
}
